/**
 * Copia um texto para a área de transferência de forma resiliente.
 *
 * A API `navigator.clipboard` só existe em contexto seguro (HTTPS ou localhost).
 * Quando o painel é acessado por HTTP (ex.: pelo IP da rede), ela fica indisponível
 * e dispara erro. Nesses casos caímos no fallback com `document.execCommand('copy')`,
 * que funciona em contexto não-seguro.
 *
 * @returns `true` se copiou com sucesso, `false` caso contrário.
 */
export async function copyToClipboard(text: string): Promise<boolean> {
    // Caminho moderno: só disponível em contexto seguro.
    if (navigator.clipboard && window.isSecureContext) {
        try {
            await navigator.clipboard.writeText(text)
            return true
        } catch {
            // Cai para o fallback abaixo.
        }
    }

    // Fallback legado para contexto não-seguro (HTTP).
    try {
        const textarea = document.createElement('textarea')
        textarea.value = text
        // Mantém fora da viewport e evita rolagem/zoom.
        textarea.style.position = 'fixed'
        textarea.style.top = '-9999px'
        textarea.style.left = '-9999px'
        textarea.setAttribute('readonly', '')
        document.body.appendChild(textarea)
        textarea.select()
        textarea.setSelectionRange(0, text.length)
        const ok = document.execCommand('copy')
        document.body.removeChild(textarea)
        return ok
    } catch {
        return false
    }
}

/**
 * Copia para a área de transferência um texto que só fica disponível após uma
 * operação assíncrona (ex.: buscar um token no backend).
 *
 * Um `await` de rede dentro do handler de clique consome a "user activation" do
 * gesto, fazendo a escrita no clipboard ser bloqueada depois. Para contornar,
 * passamos uma `Promise<Blob>` ao `ClipboardItem`: a chamada a
 * `navigator.clipboard.write` acontece de forma síncrona no clique (preservando
 * a activation) e só o conteúdo resolve mais tarde.
 *
 * Cai para o caminho clássico (buscar o texto e então copiar) quando a API não
 * está disponível.
 *
 * @returns `true` se copiou com sucesso, `false` caso contrário.
 */
export async function copyTextLazy(
    getText: () => Promise<string>,
): Promise<boolean> {
    if (
        window.isSecureContext &&
        navigator.clipboard &&
        typeof navigator.clipboard.write === 'function' &&
        typeof ClipboardItem !== 'undefined'
    ) {
        try {
            const item = new ClipboardItem({
                'text/plain': getText().then(
                    (text) => new Blob([text], { type: 'text/plain' }),
                ),
            })
            await navigator.clipboard.write([item])
            return true
        } catch {
            // Cai para o caminho clássico abaixo.
        }
    }

    try {
        const text = await getText()
        return await copyToClipboard(text)
    } catch {
        return false
    }
}
